import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Paper,
    Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination,
    TableRow, TextField, Typography,
} from '@mui/material';
import RegisterNotes from './RegisterNotes';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { ILeaveRegister } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { friendlyNote, num, triggerDownload } from './shared';
import { humanizeEnum } from '../../utils/format';

const ALLOCATION_SOURCE_LABELS: Record<string, string> = {
    POLICY: 'Leave policy',
    ACCRUAL: 'Builds up over time',
    OVERRIDE: 'Set for this employee',
};

interface Props {
    institutionId: string;
}

const EMPLOYEE_TOTALS_PAGE_SIZE = 10;

// T8 / M1 fix: `restatedColumns` carries stable API tokens (backend contract, also printed as-is
// into the XLSX note row) -- never render them raw in the UI. Map to human-readable labels for
// display; fall back to the raw token so an unmapped future addition still renders something.
const RESTATED_COLUMN_LABELS: Record<string, string> = {
    tenureGateOpen: 'tenure gate',
    policyName: 'policy name',
    entitlementBasis: 'entitlement basis',
};
const restatedColumnLabel = (token: string) => RESTATED_COLUMN_LABELS[token] ?? token;

const LeaveRegisterTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [register, setRegister] = useState<ILeaveRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalsPage, setTotalsPage] = useState(0);
    // T8: unset ('') means "use the backend's current-fiscal-year default" -- the picker starts
    // empty and is populated with the resolved current year once the first load returns (so it
    // "defaults to current" without the frontend needing to know the institution's fiscal
    // calendar itself).
    const [year, setYear] = useState<number | ''>('');

    // `yearOverride` lets the institution-switch effect force a fresh-default load without
    // racing the `setYear('')` state update it fires alongside (the Load button omits it and
    // reads the current `year` state instead).
    const load = async (yearOverride?: number | '') => {
        const effectiveYear = yearOverride !== undefined ? yearOverride : year;
        // M-3 (mirrors the other registers' year field): a cleared/mid-edit year field parses to
        // NaN -- never fire the request with it.
        if (typeof effectiveYear === 'number' && Number.isNaN(effectiveYear)) return;
        setLoading(true);
        setPage(0);
        setTotalsPage(0);
        try {
            const data = await StatutoryRegisterService.getLeaveRegister(
                institutionId, effectiveYear === '' ? undefined : effectiveYear,
            );
            setRegister(data);
            setLoadError(null);
            if (effectiveYear === '') setYear(data.fiscalYear);
        } catch (e) {
            // M-5: never leave a stale register on screen under a filter the user thinks was
            // reloaded — clear it and surface an inline error alongside the toast.
            setRegister(null);
            setLoadError('Could not load the leave register.');
            toast.showApiError(e, 'Failed to load leave register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setYear('');
        load('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        if (!register) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadLeaveRegisterXlsx(institutionId, register.fiscalYear);
            triggerDownload(blob, `leave-register-FY${register.fiscalYear}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!register) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadLeaveRegisterPdf(institutionId);
            triggerDownload(blob, `leave-register-FY${register.fiscalYear}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Display only: a leave type with nothing allocated and nothing used (e.g. Maternity Leave for a
    // male employee) is noise, so those rows are hidden. Grand totals are unaffected (they are 0 anyway).
    const visibleRows = register
        ? register.rows.filter(r => !(r.allocated === 0 && r.availed === 0 && r.encashed === 0
            && r.settled === 0 && !r.carriedIn && !r.accruedInYear))
        : [];
    const pagedRows = register
        ? visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];
    const pagedTotals = register
        ? register.employeeTotals.slice(
            totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE, totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE + EMPLOYEE_TOTALS_PAGE_SIZE,
        )
        : [];

    return (
        <Box data-help-page="statutory.leave.page">
            <Box data-help-section="statutory.leave.filter" display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    type="number"
                    data-help="statutory.leave.year"
                    value={year}
                    onChange={e => {
                        if (e.target.value === '') { setYear(''); return; }
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setYear(v);
                    }}
                    sx={{ width: 120 }}
                />
                {register && (
                    <Typography variant="subtitle1" data-testid="leave-fiscal-year-label" data-help="statutory.leave.fiscalYear">
                        {register.restatedAgainstCurrentPolicy ? 'Fiscal year' : 'Current fiscal year'}: FY{register.fiscalYear}
                    </Typography>
                )}
                <Button variant="outlined" size="small" onClick={() => load()} disabled={loading} data-help="statutory.leave.load">Load</Button>
                <Button
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                    data-help="statutory.leave.downloadXlsx"
                >
                    Download XLSX
                </Button>
                <Button
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                    data-help="statutory.leave.downloadPdf"
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && loadError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="leave-load-error">{loadError}</Alert>
            )}

            {!loading && register && (
                <>
                    {/* Shown only for a past (or otherwise non-current) fiscal year -- the
                        current fiscal year's caveats below never carry this banner. */}
                    {register.restatedAgainstCurrentPolicy && (
                        <Alert severity="warning" sx={{ mb: 1 }} data-testid="leave-restatement-banner" data-help="statutory.leave.restated">
                            FY{register.fiscalYear} is a historical fiscal year. Allocation, accrual,
                            carry-forward, availed, encashed and settled figures are historical for
                            that year, but {register.restatedColumns.map(restatedColumnLabel).join(', ')} are
                            calculated using today&apos;s leave rules, not the rules that applied then.
                        </Alert>
                    )}

                    {/* T8 / R21: the earliest-data floor -- an empty register with a named reason,
                        rendered in place of the row table entirely. */}
                    {register.emptyReason && (
                        <Alert severity="info" sx={{ mb: 1 }} data-testid="leave-empty-reason" data-help="statutory.leave.emptyReason">
                            {register.emptyReason}
                        </Alert>
                    )}

                    <RegisterNotes notes={register.caveats} testId="leave-caveats" map={friendlyNote} />

                    <TableContainer component={Paper} data-testid="leave-register-table" data-help-section="statutory.leave.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.leave.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.leave.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.leave.col.leaveType">Leave Type</TableCell>
                                    <TableCell data-help="statutory.leave.col.source">Allocation Source</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.carriedIn">Carried In</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.accrued">Accrued In Year</TableCell>
                                    {/* Label matches the XLSX sheet header exactly (M-4). */}
                                    <TableCell align="right" data-help="statutory.leave.col.allocated">Allocated (total credited, carry-forward inclusive)</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.availed">Availed</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.encashed">Encashed</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.settled">Settled</TableCell>
                                    <TableCell align="right" data-help="statutory.leave.col.remaining">Remaining</TableCell>
                                    {/* Spec §1.3 C2.4 / H-1: the tenure columns the caveat below refers to. */}
                                    <TableCell data-help="statutory.leave.col.tenureGate">Tenure Gate Open (open or not applicable)</TableCell>
                                    <TableCell data-help="statutory.leave.col.anniversary">Anniversary Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center">
                                            <Typography color="text.secondary">No rows for the current fiscal year.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map((row, idx) => (
                                    <TableRow key={`${row.userId}-${row.leaveType}-${idx}`} hover>
                                        <TableCell>{row.employeeName ?? '—'}</TableCell>
                                        <TableCell>{row.employeeCode ?? '—'}</TableCell>
                                        <TableCell>{row.leaveType}</TableCell>
                                        <TableCell>{humanizeEnum(row.allocationSource, ALLOCATION_SOURCE_LABELS)}</TableCell>
                                        <TableCell align="right">{num(row.carriedIn)}</TableCell>
                                        <TableCell align="right">{num(row.accruedInYear)}</TableCell>
                                        <TableCell align="right">{row.allocated}</TableCell>
                                        <TableCell align="right">{row.availed}</TableCell>
                                        <TableCell align="right">{row.encashed}</TableCell>
                                        <TableCell align="right">{row.settled}</TableCell>
                                        <TableCell
                                            align="right"
                                            data-testid={row.remaining < 0 ? 'leave-remaining-negative' : undefined}
                                            sx={row.remaining < 0 ? { color: 'error.main', fontWeight: 600 } : undefined}
                                        >
                                            {row.remaining}
                                        </TableCell>
                                        <TableCell>{row.tenureGateOpen ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{row.anniversaryDate ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                {/* M-6: only the Grand Total renders in the always-visible footer — a
                                    large institution's employeeTotals list lives in its own paginated
                                    section below, never inline on every page of the row table. */}
                                <TableRow data-help="statutory.leave.grandTotal" sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                                    <TableCell colSpan={4}>Grand Total</TableCell>
                                    <TableCell colSpan={2} />
                                    <TableCell align="right">{register.grandTotal.totalAllocated}</TableCell>
                                    <TableCell align="right">{register.grandTotal.totalAvailed}</TableCell>
                                    <TableCell align="right">{register.grandTotal.totalEncashed}</TableCell>
                                    <TableCell align="right">{register.grandTotal.totalSettled}</TableCell>
                                    <TableCell
                                        align="right"
                                        sx={register.grandTotal.totalRemaining < 0 ? { color: 'error.main' } : undefined}
                                    >
                                        {register.grandTotal.totalRemaining}
                                    </TableCell>
                                    <TableCell colSpan={2} />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={visibleRows.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="leave-employee-totals-section" data-help-section="statutory.leave.employeeTotals">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Employee Totals ({register.employeeTotals.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.leave.totals.col.employee">Employee</TableCell>
                                            <TableCell align="right" data-help="statutory.leave.totals.col.allocated">Allocated</TableCell>
                                            <TableCell align="right" data-help="statutory.leave.totals.col.availed">Availed</TableCell>
                                            <TableCell align="right" data-help="statutory.leave.totals.col.encashed">Encashed</TableCell>
                                            <TableCell align="right" data-help="statutory.leave.totals.col.settled">Settled</TableCell>
                                            <TableCell align="right" data-help="statutory.leave.totals.col.remaining">Remaining</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedTotals.map(t => (
                                            <TableRow key={t.userId}>
                                                <TableCell>{t.employeeName ?? t.userId}</TableCell>
                                                <TableCell align="right">{t.totalAllocated}</TableCell>
                                                <TableCell align="right">{t.totalAvailed}</TableCell>
                                                <TableCell align="right">{t.totalEncashed}</TableCell>
                                                <TableCell align="right">{t.totalSettled}</TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={t.totalRemaining < 0 ? { color: 'error.main', fontWeight: 600 } : undefined}
                                                >
                                                    {t.totalRemaining}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={register.employeeTotals.length}
                                page={totalsPage}
                                onPageChange={(_, p) => setTotalsPage(p)}
                                rowsPerPage={EMPLOYEE_TOTALS_PAGE_SIZE}
                                rowsPerPageOptions={[EMPLOYEE_TOTALS_PAGE_SIZE]}
                            />
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default LeaveRegisterTab;
